import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import exifr from "exifr";
import { ArrowLeft, Camera, Trash2, Upload, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO = "/bailey-logo.png";
const DECADES = ["1970s", "1980s", "1990s", "2000s", "2010s", "2020s"];

export default function ReunionPhotosPage() {
  const { user } = useAuth();
  const isAdmin = !!user;

  const [active, setActive] = useState(DECADES[DECADES.length - 1]); // default 2020s
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const load = () => {
    setLoading(true);
    return api
      .get("/reunion-photos")
      .then((r) => setPhotos(r.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(DECADES.map((d) => [d, []]));
    for (const p of photos) if (map[p.decade]) map[p.decade].push(p);
    return map;
  }, [photos]);

  const counts = useMemo(
    () => Object.fromEntries(DECADES.map((d) => [d, grouped[d].length])),
    [grouped]
  );

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="max-w-6xl w-full mx-auto px-6 md:px-10 pt-8 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 min-w-0">
          <img
            src={LOGO}
            alt="Bailey Family Reunion"
            className="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-[#1a2947]/10"
          />
          <span className="font-serif text-lg sm:text-2xl text-stone-900 truncate">
            Bailey Family Reunion
          </span>
        </Link>
        <Link
          to="/"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-[#e6e2d6] bg-white px-3 py-2 text-xs sm:text-sm font-medium text-[#1a2947] hover:bg-[#1a2947] hover:text-white transition-colors"
          data-testid="reunion-photos-back-home"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="hidden sm:inline">Back home</span>
        </Link>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="label-eyebrow text-[#8a7a3a] mb-3">Reunion Album</div>
        <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight mb-3">
          Every Bailey gathering, remembered.
        </h1>
        <p className="text-stone-600 max-w-2xl">
          Reunion photos organized by the decade they were taken. Pick a
          decade below to browse.
          {isAdmin && (
            <>
              {" "}
              Organizers can upload with the button under each tab.
            </>
          )}
        </p>

        <div
          className="mt-8 flex flex-wrap gap-2"
          role="tablist"
          data-testid="reunion-decade-tabs"
        >
          {DECADES.map((d) => (
            <button
              key={d}
              type="button"
              role="tab"
              onClick={() => setActive(d)}
              className={
                "inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors " +
                (active === d
                  ? "bg-[#1a2947] text-white"
                  : "bg-white text-[#1a2947] border border-[#e6e2d6] hover:bg-[#faf7ef]")
              }
              data-testid={`decade-tab-${d}`}
            >
              {d}
              <span
                className={
                  "rounded-full text-[10px] px-1.5 py-0.5 tabular-nums " +
                  (active === d
                    ? "bg-white/15 text-white"
                    : "bg-[#faf7ef] text-stone-500 border border-[#eae5d9]")
                }
              >
                {counts[d]}
              </span>
            </button>
          ))}
        </div>

        {isAdmin && (
          <AdminUploadCard
            decade={active}
            onUploaded={load}
            onSuggestDecade={(dec) => setActive(dec)}
          />
        )}

        <section
          className="mt-8"
          aria-label={`Photos from the ${active}`}
          data-testid={`decade-section-${active}`}
        >
          {loading ? (
            <div className="text-stone-500 text-sm">Loading photos…</div>
          ) : grouped[active].length === 0 ? (
            <div className="border border-dashed border-[#e6e2d6] rounded-2xl px-6 py-14 text-center text-stone-500 bg-white/50">
              <Camera className="h-6 w-6 mx-auto mb-3 text-stone-400" />
              No photos in the {active} yet.
              {isAdmin && " Upload the first one above."}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {grouped[active].map((p) => (
                <PhotoCard
                  key={p.id}
                  photo={p}
                  isAdmin={isAdmin}
                  onOpen={() => setLightbox(p)}
                  onDeleted={load}
                />
              ))}
            </div>
          )}
        </section>
      </main>

      <Dialog open={!!lightbox} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-black border-none max-w-5xl p-0">
          {lightbox && (
            <div className="relative">
              <img
                src={`${API_BASE}/reunion-photos/file/${lightbox.path}`}
                alt={lightbox.caption || "Reunion photo"}
                className="w-full max-h-[85vh] object-contain bg-black"
              />
              {lightbox.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white px-6 py-4 text-sm">
                  {lightbox.caption}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotoCard({ photo, isAdmin, onOpen, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const del = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this photo?")) return;
    setDeleting(true);
    try {
      await api.delete(`/reunion-photos/${photo.id}`);
      toast.success("Photo deleted.");
      onDeleted?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed.");
    } finally {
      setDeleting(false);
    }
  };
  return (
    <div
      className="group relative rounded-xl overflow-hidden bg-white border border-[#e6e2d6] cursor-zoom-in"
      onClick={onOpen}
      data-testid={`photo-card-${photo.id}`}
    >
      <img
        src={`${API_BASE}/reunion-photos/file/${photo.path}`}
        alt={photo.caption || "Reunion photo"}
        loading="lazy"
        className="w-full h-40 sm:h-48 object-cover group-hover:scale-[1.02] transition-transform duration-500"
      />
      {photo.caption && (
        <div className="px-3 py-2 text-xs text-stone-600 truncate">
          {photo.caption}
        </div>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={del}
          disabled={deleting}
          className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 border border-[#e6e2d6] flex items-center justify-center text-red-600 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
          aria-label="Delete photo"
          data-testid={`photo-delete-${photo.id}`}
        >
          {deleting ? "…" : <Trash2 className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

function AdminUploadCard({ decade, onUploaded, onSuggestDecade }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [detected, setDetected] = useState(null); // "1980s" etc.

  const reset = () => {
    setFile(null);
    setCaption("");
    setDetected(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const pickFile = async (f) => {
    setFile(f || null);
    setDetected(null);
    if (!f) return;
    try {
      const meta = await exifr.parse(f, ["DateTimeOriginal", "CreateDate"]);
      const dt = meta?.DateTimeOriginal || meta?.CreateDate;
      if (dt instanceof Date && !Number.isNaN(dt.getTime())) {
        const year = dt.getFullYear();
        if (year >= 1970 && year <= 2029) {
          const dec = `${Math.floor(year / 10) * 10}s`;
          setDetected(dec);
          if (dec !== decade) onSuggestDecade?.(dec);
        }
      }
    } catch {
      // EXIF absent or unreadable — silently continue.
    }
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("decade", decade);
      fd.append("caption", caption);
      await api.post("/reunion-photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded to ${decade}.`);
      reset();
      onUploaded?.();
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) || "Upload failed."
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <form
      onSubmit={upload}
      className="mt-6 bg-white border border-[#e6e2d6] rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-end gap-3"
      data-testid="reunion-upload-form"
    >
      <div className="flex-1 min-w-0">
        <div className="label-eyebrow text-[#1a2947] mb-1">
          Upload to {decade}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1.4fr] gap-3">
          <div>
            <Label htmlFor="rp-file" className="sr-only">
              Photo
            </Label>
            <Input
              id="rp-file"
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => pickFile(e.target.files?.[0] || null)}
              className="bg-white border-stone-200 file:mr-3 file:rounded-full file:border-0 file:bg-[#faf7ef] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-[#1a2947]"
              data-testid="reunion-upload-file"
              required
            />
            {detected && (
              <div
                className="mt-1 text-[11px] text-[#8a7a3a]"
                data-testid="reunion-upload-detected"
              >
                EXIF says {detected} — decade tab switched for you.
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="rp-caption" className="sr-only">
              Caption
            </Label>
            <Input
              id="rp-caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Caption (optional) — who, where, or the year"
              className="bg-white border-stone-200"
              data-testid="reunion-upload-caption"
              maxLength={280}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {file && (
          <button
            type="button"
            onClick={reset}
            className="text-stone-500 hover:text-stone-700 p-1"
            aria-label="Clear selection"
            data-testid="reunion-upload-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <Button
          type="submit"
          disabled={!file || uploading}
          className="rounded-full bg-[#1a2947] hover:bg-[#0f1a2e] text-white px-5 h-11"
          data-testid="reunion-upload-submit"
        >
          <Upload className="h-4 w-4 mr-2" />
          {uploading ? "Uploading…" : "Upload"}
        </Button>
      </div>
    </form>
  );
}
